import { Order, SortOrder } from "constants/enums";
import * as XLSX from "xlsx";
import chroma from "chroma-js";
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from "dayjs";

export const formatToday = () => dayjs().format("YYYY-MM-DD");

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export const getUserStatusBadgeStyle = (
  status: number
): {
  color: string;
  backgroundColor: string;
} => {
  switch (status) {
    case 1:
      return {
        color: "primary.main",
        backgroundColor: "#3A974C",
      };
    case 2:
      return {
        color: "#F29339",
        backgroundColor: "#F29339",
      };

    default:
      return {
        color: "primary.main",
        backgroundColor: "#3A974C",
      };
  }
};

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === SortOrder.DESC
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function isValidDomain(domain: string) {
  var regex =
    /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/i;
  return regex.test(domain);
}
export function exportXLSX(params: any) {
  const { data, headCells, filename } = params;
  const heads: any = [];
  const rows: any = [];

  let exportData = data;

  ["Label", "Value"].forEach((headCell: string) => {
    heads.push(headCell);
    // heads.push(t('header.' + headCell.label));
  });

  rows.push(heads);

  exportData.forEach((row: any, index: number) => {
    rows.push([headCells[index], row]);
  });

  try {
    const wb = XLSX.utils.book_new();
    const newWs = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, newWs);
    const rawExcel = XLSX.write(wb, { type: "base64" });

    const encodedUri = encodeURI(rawExcel);
    const link = document.createElement("a");
    link.setAttribute(
      "href",
      "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
        encodedUri
    );
    link.setAttribute("download", `${filename}.xlsx`);
    document.body.appendChild(link); // Required for FF

    link.click();
  } catch (err) {
    console.error(err);
  }
}

export function convertTimeToISOString(timeStr:string) {
  // Today's date
  const today = new Date();
  const [hours, minutes, seconds] = timeStr?.split(":");

  // Set hours, minutes, and seconds based on the provided time
  today.setHours(+hours);
  today.setMinutes(+minutes);
  today.setSeconds(+seconds);

  // Return the ISO string in UTC format
  return today.toISOString();
}

/**
 * Convert string hours to it's equivalent in numbers
 *
 * @param hours hours: string
 * @param format format: string
 *
 * @returns number
 */
export const hoursStringToNumber = (hours: string, format: string): number => {
  const time = hours.split(":")[0];

  return format === "PM"
    ? parseInt(time) + 12 === 24
      ? 12
      : parseInt(time) + 12
    : parseInt(time) === 12
    ? 0
    : parseInt(time);
};

/**
 * Compare giver hour in number to current locale hour
 *
 * @param hour hour: number
 *
 * @returns number
 */
export const compareHoursToCurrent = (hour: number): number => {
  const d = new Date();
  let currentHour = d.getHours();

  return hour - currentHour;
};

/**
 * Capitalize first letter of a given string
 *
 * @param string string: string
 *
 * @returns string
 */
export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function stableSort(array: readonly any[], comparator: any) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function randomStr(length: number) {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

export const formatAMPM = (isoDate: string) => {
  const date = new Date(isoDate);
  let hours: string | number = date.getHours();
  let minutes: string | number = date.getMinutes();
  const ampm = hours >= 12 ? "pm " : "am";

  hours %= 12;
  hours = hours || 12;
  minutes = minutes < 10 ? `0${minutes}` : minutes;

  const strTime = `${hours}:${minutes} ${ampm}`;

  return strTime;
};

export function getDayNumber(dateStr: string, locale: string) {
  const date = new Date(dateStr);
  return date.getDate();
}

export function getDateFormatted(date: Date) {
  const today = date ? new Date(date) : new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();

  return yyyy + "- " + mm + "- " + dd;
}

export function capitalizeWords(string: string) {
  return string.replace(/(?:^|\s)\S/g, function (a) {
    return a.toUpperCase();
  });
}

export const mergeListOfStringsByDash = (list: string[] | null) => {
  if (list !== null && list.length)
    return list.reduce((prev, current) => `${prev} - ${current}`);

  return "";
};

export const mergeListOfStrings = (
  list: (string | null)[],
  delimiter: string
) => {
  if (list !== null && list.length)
    return list.reduce((prev, current) => `${prev} ${delimiter} ${current}`);

  return "";
};

export const makeFakePhoneNumbers = (index: number, length: number) => {
  var result = `01${index}`;
  var characters = "0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const getDomainName = () => {
  if (window.location.hostname === "localhost") return "itzone";
  return "itzone";
};

export const createArray = (size: number) =>
  Array.from({ length: size }, (_, i) => i);

export function convertToCamelCase(text: string) {
  return text.replace(/-([a-z])/g, function (g) {
    return g[1].toUpperCase();
  });
}

export function extractSelectedCheckboxes(keyName: string, data: any) {
  const selected: string[] = [];

  for (let key of Object.getOwnPropertyNames(data)) {
    if (key.includes(keyName)) {
      if (data[key] === true) {
        selected.push(key.replace(keyName, ""));
      }
    }
  }

  return selected;
}

export function validatePhone(phone: string) {
  var re = /^[0][1-9]\d{9}$|^[1-9]\d{9}$/;
  return re.test(phone);
}

export function convertToSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");
}

export function camelCaseToSpaces(text: string): string {
  const result: string = text.replace(/([A-Z])/g, "$1");
  return result.charAt(0).toUpperCase() + result.slice(1);
}

/**
  The pxToRem() function helps you to convert a px unit into a rem unit,
 */
export function pxToRem(number: number, baseNumber: number = 20): string {
  return `${number / baseNumber}rem`;
}

export function shuffleArray(array: any[]) {
  let currentIndex = array.length;

  while (currentIndex !== 0) {
    const randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}

/**
  The hexToRgb() function helps you to change the hex color code to rgb
  using chroma-js library.
 */

/**
  The linearGradient() function helps you to create a linear gradient color background
 */

export function linearGradient(
  color: string,
  colorState: string,
  angle: number = 195
): string {
  return `linear-gradient(${angle}deg, ${color}, ${colorState})`;
}

export function excerpt(str: string, maxLength: number): string {
  if (str.length > maxLength) return str.substring(0, maxLength) + "...";

  return str;
}
/**
  The rgba() function helps you to create a rgba color code, it uses the hexToRgb() function
  to convert the hex code into rgb for using it inside the rgba color format.
 */

/**
  The gradientChartLine() function helps you to create a gradient color for the chart line
 */

/**
  The boxShadow() function helps you to create a box shadow for an element
 */

export function getDayTime(): string {
  const today: Date = new Date();
  const curHr: number = today.getHours();

  if (curHr < 12) {
    return "morning";
  } else if (curHr < 18) {
    return "afternoon";
  } else {
    return "evening";
  }
}

export const removeUndefined = (obj: any) => {
  Object.keys(obj).forEach((key) =>
    obj[key] === undefined ? delete obj[key] : {}
  );
  return obj;
};
/**
 * Reformats a number into a currency string
 *
 * @param number number: number
 *
 * @returns string
 */
export default function getCurrencyFormat(number: number): string {
  const formatter = Intl.NumberFormat("en", {
    maximumSignificantDigits: 3,
    style: "currency",
    currency: "USD",
  });
  return formatter.format(number);
}

export const handleError = (error: any): string => {
  if (typeof error === "string") return error;

  // PRIORITY 1: Check if error.message is set and is specific (from our custom error handling)
  if (error?.message && 
      error.message !== "Network Error" && 
      !error.message.includes("Network Error") && 
      !error.message.includes("timeout") &&
      error.message !== "Something went wrong. Please check your network and try again.") {
    return error.message;
  }

  // PRIORITY 2: Check error.response.data
  if (error?.response?.data) {
    let data = error.response.data;

    // If data is a Blob, fallback to error.message or generic
    if (data instanceof Blob) {
      return error?.message || "Something went wrong. Please check your network and try again.";
    }

    // Check if there's a structured `errors` object
    if (data.errors && typeof data.errors === "object") {
      const allMessages = Object.values(data.errors)
        .flat() // Flatten the arrays of messages
        .join(", "); // Join all messages into a single string
      return allMessages;
    }

    // Return other possible fields
    const message = data.message || data.error || data.englishMessage || data.errorMessage || data.title;
    if (message) {
      return message;
    }
    
    // If data is a string, use it
    if (typeof data === 'string') {
      return data;
    }
    
    // Last resort: stringify the data
    const stringified = JSON.stringify(data);
    if (stringified !== '{}' && stringified !== 'null') {
      return stringified;
    }
  }

  // PRIORITY 3: Check if there's a request but no response
  if (error?.request && !error?.response) {
    return "Something went wrong. Please check your network and try again.";
  }

  // PRIORITY 4: Fallback to error.message or generic message
  return error?.message || "Something went wrong. Please check your network and try again.";
};

type RGB = {
  r: number;
  g: number;
  b: number;
};

const hexToRgb = (hex: string): RGB => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

const rgbToHex = ({ r, g, b }: RGB): string => {
  const toHex = (x: number) => x.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const adjustColor = (color: RGB, factor: number): RGB => {
  return {
    r: Math.min(255, Math.max(0, color.r + factor)),
    g: Math.min(255, Math.max(0, color.g + factor)),
    b: Math.min(255, Math.max(0, color.b + factor)),
  };
};

export const generateColorShades = (baseColor: string): string[] => {
  const baseRgb = hexToRgb(baseColor);
  const factor = 40; // Change this to adjust the step size

  return [
    rgbToHex(adjustColor(baseRgb, 2 * factor)), // Lighter color
    rgbToHex(adjustColor(baseRgb, factor)), // Light color
    baseColor, // Base color
    rgbToHex(adjustColor(baseRgb, -factor)), // Dark color
    rgbToHex(adjustColor(baseRgb, -2 * factor)), // Darker color
  ];
};


export function isValidEmail(email:string) {
  const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
  return emailRegex.test(email);
}

export const generateColorGradient = (
  baseColor: string = "#0000FF",
  num: number = 10,
  brighten: number = 3,
  darken: number = 3
): string[] => {
  const scale = chroma
    .scale([
      chroma(baseColor).brighten(brighten),
      baseColor,
      chroma(baseColor).darken(darken),
    ])
    .mode("lab")
    .colors(num);
  return scale;
};
interface ExportToExcelProps<T> {
  data: T[];
  headers: string[];
  fileName?: string;
  formatValue?: (key: string, value: any, row: T) => any;
}

export const exportToExcel = <T extends Record<string, any>>({
  data,
  headers,
  fileName = 'data.xlsx',
  formatValue,
}: ExportToExcelProps<T>) => {
  // Helper to prettify header names
  const prettifyHeader = (header: string) => {
    return header
      .replace(/([a-z])([A-Z])/g, '$1 $2') // split camelCase → "camel Case"
      .replace(/_/g, ' ')                  // replace underscores
      .replace(/\s+/g, ' ')                // clean extra spaces
      .trim()
      .replace(/^\w/, (c) => c.toUpperCase()); // capitalize first letter
  };

  // Filter + format data
  const filteredData = data.map((item) =>
    headers.reduce((obj, key) => {
      let value = item[key];
      if (formatValue) value = formatValue(key, value, item);
      obj[key] = value;
      return obj;
    }, {} as Record<string, any>)
  );

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(filteredData, { header: headers });

  // Prettify header row (replace raw keys with formatted ones)
  const prettyHeaders = headers.map(prettifyHeader);
  XLSX.utils.sheet_add_aoa(worksheet, [prettyHeaders], { origin: 'A1' });

  // Apply bold style to header row
  prettyHeaders.forEach((_, index) => {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
    const cell = worksheet[cellAddress] as XLSX.CellObject | undefined;
    if (cell) {
      const style = (cell.s || {}) as any;
      style.font = {
        ...(style?.font || {}),
        bold: true,
      };
      style.alignment = {
        ...(style?.alignment || {}),
        horizontal: 'center',
        vertical: 'center',
      };
      cell.s = style;
    }
  });

  // Adjust column widths based on content
  const columnWidths = headers.map((header, index) => {
    const headerLength = prettyHeaders[index]?.length ?? header.length;
    const maxCellLength = filteredData.reduce((max, row) => {
      const value = row[header];
      const cellLength = value !== undefined && value !== null
        ? value.toString().length
        : 0;
      return Math.max(max, cellLength);
    }, headerLength);

    const baseWidth = Math.max(headerLength, maxCellLength) + 2;
    // clamp width to reasonable range
    return { wch: Math.min(Math.max(baseWidth, 12), 40) };
  });

  worksheet['!cols'] = columnWidths;

  // Write workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, fileName);
};

interface ExportToPDFProps<T> {
  data: T[];
  headers: string[];
  fileName?: string;
  formatValue?: (key: string, value: any, row: T) => any;
}

export const exportToPDF = <T extends Record<string, any>>({
  data,
  headers,
  fileName = 'data.pdf',
  formatValue,
}: ExportToPDFProps<T>) => {
  // Helper: prettify headers
  const prettifyHeader = (header: string) =>
    header
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^\w/, (c) => c.toUpperCase());

  const formattedHeaders = headers.map(prettifyHeader);

  // Format + map data
  const tableData = data.map((item) =>
    headers.map((key) => {
      let value = item[key];
      if (formatValue) value = formatValue(key, value, item);
      if (Array.isArray(value)) return value.join(', ');
      if (typeof value === 'object' && value !== null) return JSON.stringify(value);
      return value ?? '';
    })
  );

  // Create PDF document
  const doc = new jsPDF({
    orientation: 'landscape', // better for tables
    unit: 'pt',
    format: 'A4',
  });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.text('Exported Data', 40, 40);

  autoTable(doc, {
    startY: 60,
    head: [formattedHeaders],
    body: tableData,
    styles: {
      fontSize: 10,
      cellPadding: 6,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: [60, 120, 216], // nice blue
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  });

  doc.save(fileName);
};
